import { useEffect, useReducer, useState } from 'react';

import ErrorMessage from '@/components/common/ErrorMessage';
import MultiStepFormStepper from '@/components/common/multi-step-form/MultiStepFormStepper';
import { StepItem, useStepper } from '@/core/hooks/useStepper';
import { BasicResponse } from '@/core/models/common-types';

import { userSignupReducer, initialUserSignupState, UserSignupState } from '../states/userSignupReducer';
import UserSignupRequestOtpStep from '../steps/UserSignupRequestOtpStep';
import UserSignupVerifyOtpStep from '../steps/UserSignupVerifyOtpStep';
import UserSignupCompleteStep from '../steps/UserSignupCompleteStep';

type Step = 'request-otp' | 'verify-otp' | 'complete-signup';

export const updateStep = (step: Step) => {
  const url = new URL(window.location.href);
  url.searchParams.set('step', step);
  window.history.replaceState({}, '', url);
};

interface Props {
  id: string;
}

const UserSignupForm: React.FC<Props> = ({ id }) => {
  const [error] = useState<BasicResponse | null>(null);

  const steps: StepItem<Step>[] = [
    { key: 'request-otp', label: 'Request OTP' },
    { key: 'verify-otp', label: 'Verify OTP' },
    { key: 'complete-signup', label: 'Complete Signup' },
  ];

  const { labels, currentKey, currentIndex, next, prev } = useStepper<Step>(steps, {
    initial: 'request-otp',
    visibilityDeps: [],
  });

  const [state, dispatch] = useReducer(userSignupReducer, initialUserSignupState);

  useEffect(() => {
    dispatch({ type: 'UPDATE', payload: { id: id } });
  }, []);

  const handleNext = (values: Partial<UserSignupState>) => {
    dispatch({
      type: 'UPDATE',
      payload: values,
    });

    next();
    //updateStep(go);
  };

  const handleSubmit = (values: Partial<UserSignupState>) => {
    dispatch({
      type: 'UPDATE',
      payload: values,
    });

    console.log('Submitting signup:', {
      ...state,
      ...values,
    });
  };

  useEffect(() => {
    console.log('signup state =>', state);
  }, [state]);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-xl-6 col-lg-7 col-md-9">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <img src="/images/logo.png" alt="Logo" className="img-fluid mb-3" style={{ maxHeight: 100 }} />

                <h2 className="fw-bold mb-2">Create Your Account</h2>

                <p className="text-muted mb-0">Complete the steps below to create your account.</p>
              </div>

              <div className="mb-4">
                <MultiStepFormStepper steps={labels} currentStep={currentIndex} />
              </div>

              <ErrorMessage error={error} />

              <div className="mt-4">
                {currentKey === 'request-otp' && (
                  <UserSignupRequestOtpStep dispatch={dispatch} state={state} id={id} onNext={handleNext} />
                )}

                {currentKey === 'verify-otp' && (
                  <UserSignupVerifyOtpStep
                    dispatch={dispatch}
                    state={state}
                    id={id}
                    onNext={handleNext}
                    onPrevious={prev}
                  />
                )}

                {currentKey === 'complete-signup' && (
                  <UserSignupCompleteStep
                    id={id}
                    onPrevious={prev}
                    onSubmit={handleSubmit}
                    dispatch={dispatch}
                    state={state}
                  />
                )}
              </div>

              <hr className="my-4" />

              <div className="text-center">
                <span className="text-muted">Already have an account?</span>

                <a href="/auth/login" className="ms-2 fw-semibold text-decoration-none">
                  Sign In
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSignupForm;
